import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Clock, Baby, Split, Layers, Pencil, Check, X } from "lucide-react";
import { ESTADOS_RESERVA, TIPOS_MODALIDAD } from "@/lib/constants";
import { ALERTA, formatFechaLarga } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";
import { PasajeroRow } from "@/components/agenda/SalidaDetailSheet";
import ReservaFormDialog from "@/components/reservas/ReservaFormDialog";
import CambiarModalidadDialog from "@/components/agenda/CambiarModalidadDialog";
import HistorialReservaDialog from "@/components/agenda/HistorialReservaDialog";
import MoverReservaGrupoDialog from "@/components/agenda/MoverReservaGrupoDialog";

const ESTADOS_ACTIVOS = ["pendiente", "confirmada", "reprogramada"];

function miembroLabel(m) {
  const modalidad = TIPOS_MODALIDAD[m.modalidad] || m.modalidad;
  const veh = m.vehiculo === "van" ? "Van" : "Bus";
  const idioma = m.idioma === "combinada" ? "Bilingüe" : m.idioma === "en" ? "EN" : "ES";
  const prefijo = m.tour_nombre ? `${nombreTourDisplay(m.tour_nombre, m.modalidad)} · ` : "";
  return `${prefijo}${modalidad} · ${veh} · ${idioma}`;
}

function MiembroCupoBadge({ m, onSaved }) {
  const [editando, setEditando] = useState(false);
  const [val, setVal] = useState(String(m.cupo_max));
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    const nuevo = parseInt(val, 10);
    if (isNaN(nuevo) || nuevo < 0) {
      toast.error("Ingresá un cupo válido.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/salidas/${m.id}/cupo`, { cupo_max: nuevo });
      toast.success(`Cupo de "${miembroLabel(m)}" actualizado a ${nuevo} (solo esta fecha).`);
      setEditando(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (editando) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700"
        data-testid={`grupo-miembro-editando-${m.id}`}
      >
        {miembroLabel(m)} ·
        <Input
          autoFocus
          type="number"
          min={0}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && guardar()}
          className="h-5 w-14 px-1 text-[10px]"
          data-testid={`cupo-miembro-input-${m.id}`}
        />
        <button
          onClick={guardar}
          disabled={saving}
          className="text-indigo-700 hover:text-indigo-900"
          data-testid={`cupo-miembro-guardar-${m.id}`}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        </button>
        <button
          onClick={() => { setEditando(false); setVal(String(m.cupo_max)); }}
          className="text-slate-400 hover:text-slate-600"
          data-testid={`cupo-miembro-cancelar-${m.id}`}
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  }

  return (
    <button
      onClick={() => { setVal(String(m.cupo_max)); setEditando(true); }}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors group"
      data-testid={`grupo-miembro-${m.id}`}
      title="Click para editar el cupo de esta modalidad (solo esta fecha)"
    >
      {miembroLabel(m)} · {m.cupo_usado}/{m.cupo_max}
      <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

export default function GrupoDetailSheet({ open, onOpenChange, grupoId, tours, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalReserva, setModalReserva] = useState(null);
  const [histId, setHistId] = useState(null);
  const [moverReserva, setMoverReserva] = useState(null);

  const load = useCallback(async () => {
    if (!grupoId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/agenda/grupo/${grupoId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [grupoId]);

  useEffect(() => {
    if (open && grupoId) load();
  }, [open, grupoId, load]);

  const refetch = () => {
    load();
    onChanged?.();
  };

  const marcarPagado = async (r) => {
    setBusy(true);
    try {
      const total = Number(r.monto_pagado || 0) + Number(r.saldo_pendiente || 0);
      await api.post(`/reservas/${r.id}/saldo`, { saldo_pendiente: 0, monto_pagado: total });
      toast.success("Saldo puesto en cero.");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cancelar = async (r) => {
    setBusy(true);
    try {
      await api.post(`/reservas/${r.id}/estado`, { nuevo_estado: "cancelada", motivo: "Cancelada desde la agenda" });
      toast.success("Reserva cancelada. Se liberó el cupo del grupo.");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const separar = async () => {
    setBusy(true);
    try {
      await api.post(`/salidas/grupo/${grupoId}/separar`);
      toast.success("Horario separado. Cada salida vuelve a mostrar su propio cupo.");
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const g = data?.grupo;
  const a = g ? ALERTA[g.alerta] || ALERTA.verde : ALERTA.verde;
  const reservas = data?.reservas || [];
  const activas = reservas.filter((r) => ESTADOS_ACTIVOS.includes(r.estado) && !r.requiere_revision);
  const inactivas = reservas.filter((r) => !ESTADOS_ACTIVOS.includes(r.estado) || r.requiere_revision);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="grupo-detail-sheet">
          {loading || !g ? (
            <div className="flex flex-col h-full">
              <SheetHeader className="sr-only">
                <SheetTitle>Detalle del horario combinado</SheetTitle>
                <SheetDescription>Cargando información del grupo.</SheetDescription>
              </SheetHeader>
              <div className="flex items-center justify-center flex-1">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            </div>
          ) : (
            <div>
              <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-5">
                <SheetHeader className="text-left space-y-0">
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Clock className="h-4 w-4" />
                    <span className="font-bold text-slate-900 text-base tabular-nums">{g.hora}</span>
                    <span className="capitalize">{formatFechaLarga(g.fecha)}</span>
                  </div>
                  <SheetTitle className="font-heading text-xl mt-1">{nombreTourDisplay(g.tour_nombre, g.modalidad)}</SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[10px] gap-1 border-indigo-200 text-indigo-700 bg-indigo-50" data-testid="grupo-badge-combinado">
                        <Layers className="h-3 w-3" /> Horario combinado · {g.miembros.length} salidas
                      </Badge>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${a.chip}`}>
                        {a.label}
                      </span>
                    </div>
                  </SheetDescription>
                  {g.nota && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mt-2">
                      {g.nota}
                    </p>
                  )}
                </SheetHeader>

                <div className="mt-4">
                  <div className="flex items-end justify-between mb-1.5">
                    <span className="text-xs text-slate-500 self-center">Cupo total real (evita doble conteo)</span>
                    <span className={`text-3xl font-black tabular-nums leading-none ${a.text}`} data-testid="grupo-ocupacion">
                      {g.cupo_usado} <span className="text-lg font-bold text-slate-400">/ {g.cupo_max || "—"}</span>
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${a.bar} transition-all duration-500`}
                      style={{ width: `${g.cupo_max ? Math.min(100, (g.cupo_usado / g.cupo_max) * 100) : 0}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">
                    Desglose por modalidad/idioma — click en un cuadro para editar su propio tope (solo esta fecha).
                    Si el TOTAL está lleno, se cierra todo aunque un cuadro tenga lugar; si un cuadro está lleno,
                    se cierra solo esa modalidad aunque el total tenga lugar.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="grupo-desglose">
                    {g.miembros.map((m) => (
                      <MiembroCupoBadge key={m.id} m={m} onSaved={refetch} />
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={separar}
                    disabled={busy}
                    className="gap-1.5"
                    data-testid="separar-grupo-btn"
                  >
                    <Split className="h-4 w-4" /> Separar horario
                  </Button>
                </div>
              </div>

              <div className="p-5 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Pasajeros · {activas.length} reserva(s) activa(s) en el grupo
                </p>
                {activas.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Sin reservas activas en este grupo.</p>
                ) : (
                  activas.map((r) => (
                    <PasajeroRow
                      key={r.id}
                      r={r}
                      onEdit={setEditing}
                      onModalidad={setModalReserva}
                      onPagado={marcarPagado}
                      onCancelar={cancelar}
                      onHistorial={(rr) => setHistId(rr.id)}
                      onMover={setMoverReserva}
                      miembrosGrupo={g.miembros}
                    />
                  ))
                )}
                {inactivas.length > 0 && (
                  <div className="pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                      Canceladas / a revisar ({inactivas.length})
                    </p>
                    <div className="space-y-2 opacity-70">
                      {inactivas.map((r) => (
                        <PasajeroRow
                          key={r.id}
                          r={r}
                          onEdit={setEditing}
                          onModalidad={setModalReserva}
                          onPagado={marcarPagado}
                          onCancelar={cancelar}
                          onHistorial={(rr) => setHistId(rr.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 pt-1 inline-flex items-center gap-1">
                  <Baby className="h-3.5 w-3.5" /> {data.infantes_total} infante(s) en total en el grupo.
                </p>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ReservaFormDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} tours={tours} reserva={editing} onSaved={refetch} />
      <CambiarModalidadDialog open={!!modalReserva} onOpenChange={(v) => !v && setModalReserva(null)} reserva={modalReserva} onChanged={refetch} />
      <HistorialReservaDialog open={!!histId} onOpenChange={(v) => !v && setHistId(null)} reservaId={histId} />
      <MoverReservaGrupoDialog
        open={!!moverReserva}
        onOpenChange={(v) => !v && setMoverReserva(null)}
        reserva={moverReserva}
        miembros={g?.miembros || []}
        onMoved={refetch}
      />
    </>
  );
}
